import type { Editor } from '@tiptap/core';
import type { Signature } from '@/types/email';

export function decodeHtmlEntities(value: string): string {
  if (!value) {
    return '';
  }
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

export function normalizeSignatureHtml(html: string): string {
  const decoded = decodeHtmlEntities(html);
  if (typeof document === 'undefined') {
    return decoded;
  }

  const container = document.createElement('div');
  container.innerHTML = decoded;

  if (
    container.childElementCount === 1 &&
    container.firstElementChild instanceof HTMLElement &&
    container.firstElementChild.tagName === 'DIV'
  ) {
    return container.firstElementChild.innerHTML.trim();
  }

  return container.innerHTML.trim();
}

export function expandSignatureBlocks(html: string): string {
  if (typeof document === 'undefined') {
    return html;
  }

  const template = document.createElement('template');
  template.innerHTML = html;

  const signatureElements = template.content.querySelectorAll<HTMLElement>('div.signature-block[data-signature-html]');
  signatureElements.forEach((element) => {
    const encoded = element.getAttribute('data-signature-html');
    if (!encoded) {
      return;
    }
    try {
      const decoded = decodeURIComponent(encoded);
      element.innerHTML = decoded;
      element.removeAttribute('data-signature-html');
    } catch (error) {
      console.warn('[signatureUtils] Failed to decode signature HTML attribute', error);
    }
  });

  return template.innerHTML;
}

export function removeSignatureBlockFromEditor(editor: Editor | null): boolean {
  if (!editor) {
    return false;
  }

  const ranges: Array<{ from: number; to: number }> = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'signatureBlock') {
      ranges.push({ from: pos, to: pos + node.nodeSize });
    }
  });

  if (ranges.length === 0) {
    return false;
  }

  ranges
    .sort((a, b) => b.from - a.from)
    .forEach(({ from, to }) => {
      editor.commands.deleteRange({ from, to });
    });

  return true;
}

interface InsertSignatureBlockOptions {
  normalizedHtml?: string;
}

export function insertSignatureBlockIntoEditor(
  editor: Editor | null,
  signature: Signature,
  options: InsertSignatureBlockOptions = {},
): string | null {
  if (!editor) {
    return null;
  }

  const { normalizedHtml } = options;
  const html = normalizedHtml ?? normalizeSignatureHtml(signature.html ?? '');

  removeSignatureBlockFromEditor(editor);

  const { state, view } = editor;
  const signatureNodeType = state.schema.nodes.signatureBlock;
  const paragraphNodeType = state.schema.nodes.paragraph;

  if (!signatureNodeType || !paragraphNodeType) {
    console.warn('[signatureUtils] signatureBlock or paragraph node is unavailable in the editor schema');
    return null;
  }

  let insertPos = state.doc.content.size;
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'quotedContentBlock') {
      insertPos = pos;
      return false;
    }
    return undefined;
  });

  const signatureNode = signatureNodeType.create({
    signatureId: signature.id,
    html,
  });

  const nodesToInsert = [signatureNode, paragraphNodeType.create()];
  const transaction = state.tr.insert(insertPos, nodesToInsert).scrollIntoView();
  view.dispatch(transaction);

  return html;
}
